/**
 * 新的流式聊天事件处理器
 * 与后端事件完全对齐，简化逻辑
 */

import { atom } from 'jotai';
import {
  messagesAtom,
  selectedConversationIdAtom,
  fetchConversationsAtom,
  updateSelectedConversationAtom,
  updateConversationAtom
} from '../chatAtoms';
import { navigateAtom } from '../navigationAtoms';
import { recordFirstTokenAtom, firstTokenReceivedAtom } from '../responseTimeAtoms';
import { Message, ProcessStep } from '../../../types';
import { messageUtils } from '../messageUtils';
import {
  InitEvent,
  ContentBlockStartEvent,
  TextDeltaEvent,
  ToolResultEvent,
  ToolErrorEvent,
  ConversationTitleGeneratedEvent,
  MessageStopEvent
} from '../../../types/streamingEventTypes';

// 新的流式聊天事件处理器（简化版，与后端事件对齐）
export const handleStreamingEventAtom = atom(
  null,
  async (get, set, event: { type: string; data: any }) => {
    const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

    try {
      // console.log('handleStreamingEventV2:', event.type, eventData);

      switch (event.type) {
        case 'init': {
          const data = eventData as InitEvent;
          // 移除对 streamingChatStateAtom 的依赖

          // 重置首个token接收标志
          set(firstTokenReceivedAtom, false);

          console.log(`init_data:`, data);
          const currentConvId = get(selectedConversationIdAtom);
          if (!currentConvId) {
            console.log('Conversation is Undefined! Updating to new conversation:', data.conversationId);
            set(updateConversationAtom, {
              conversationId: data.conversationId,
              updates: {}
            });
            set(selectedConversationIdAtom, data.conversationId);
            console.log('Conversation Updated to:', get(selectedConversationIdAtom));
            set(navigateAtom, { path: `/v3/chat-detail/${data.conversationId}`, replace: true });
          } else {
            // 分步更新策略：先删除临时消息，再添加真实消息
            // 视觉效果：2个消息 → 0个消息 → 2个消息（避免闪烁到4个消息）

            // 第1步：获取临时消息的完整数据，用于创建真实消息
            const currentMessages = get(messagesAtom);
            const conversationMessages = currentMessages[data.conversationId] || [];

            const tempUserMessage = conversationMessages.find(m => m.id === data.frontendUserMessageId);
            const tempAssistantMessage = conversationMessages.find(m => m.id === data.frontendAssistantMessageId);

            if (!tempUserMessage || !tempAssistantMessage) {
              console.warn('找不到临时消息，跳过分步更新');
              return;
            }

            // 第2步：删除临时消息（2个 → 0个）
            set(messagesAtom, (prev) =>
              messageUtils.removeMessages(prev, data.conversationId, [
                data.frontendUserMessageId,
                data.frontendAssistantMessageId
              ])
            );

            // 第3步：创建真实消息
            const realUserMessage: Message = {
              ...tempUserMessage,
              id: data.userMessageId,
              conversationId: data.conversationId,
            };

            const realAssistantMessage: Message = {
              ...tempAssistantMessage,
              id: data.messageId,
              conversationId: data.conversationId,
              parentMessageId: data.userMessageId,
              status: 'streaming',
              metadata: {
                ...(tempAssistantMessage.metadata || {}),
                processingStartTime: data.timestamp,
              }
            };

            // 第4步：添加真实消息（0个 → 2个）
            set(messagesAtom, (prev) =>
              messageUtils.addMessages(prev, data.conversationId, [
                realUserMessage,
                realAssistantMessage
              ])
            );

            // 更新对话的更新时间
            set(updateSelectedConversationAtom, {
              updatedAt: data.timestamp
            });
          }
          break;
        }

        case 'content_block_start': {
          const data = eventData as ContentBlockStartEvent;

          // 无状态处理：不需要更新 streamingChatStateAtom
          // 每个后续事件都会包含足够的信息（index + messageId）

          console.log(`content_block_start: ${data.blockType} block at index ${data.index}`);

          // 如果需要，可以在这里预初始化内容块，但通常 text_delta 会处理
          break;
        }

        case 'text_delta': {
          const data = eventData as TextDeltaEvent;

          // 重构：text_delta 先更新到 processLog，而不是直接更新 message.content
          const { text: textDelta, messageId, conversationId, index } = data;

          // 记录首个token响应时间
          set(recordFirstTokenAtom);

          set(messagesAtom, (prev) =>
            messageUtils.updateProcessLog(prev, conversationId, messageId, (processLog) => {
              // 查找当前 index 对应的 text 类型的 processLog 条目
              const existingIndex = processLog.findIndex(
                step => step.type === 'text' && step.sequence === index
              );

              if (existingIndex !== -1) {
                // 更新现有的 text 步骤，追加文本
                const updatedLog = [...processLog];
                const existingStep = updatedLog[existingIndex] as ProcessStep & { type: 'text' };
                updatedLog[existingIndex] = {
                  ...existingStep,
                  text: existingStep.text + textDelta,
                  timestamp: new Date().toISOString(), // 更新时间戳以触发渲染
                };
                return updatedLog;
              } else {
                // 创建新的 text 步骤
                const textStep: ProcessStep = {
                  type: 'text',
                  text: textDelta,
                  sequence: index,
                  timestamp: new Date().toISOString(),
                };
                return [...processLog, textStep];
              }
            })
          );
          break;
        }

        // text_block_stop 事件已被移除
        // 后端不再发送此事件到前端，只用于数据库记录
        // 前端通过 text_delta 事件的 index 变化来判断新文本块的开始

        case 'tool_result': {
          const data = eventData as ToolResultEvent & { index?: number };

          // 无状态处理：直接使用事件中的数据，保持后端发送的原始字段名
          const { toolId, toolName, status, result, timestamp, messageId, conversationId, index } = data;

          set(messagesAtom, (prev) =>
            messageUtils.updateProcessLog(prev, conversationId, messageId, (processLog) => {
              // 检查是否已经存在相同 toolId 的 tool_result 步骤
              const existingIndex = processLog.findIndex(
                step => step.type === 'tool_result' && step.toolId === toolId
              );

              const toolResultStep: ProcessStep = {
                type: 'tool_result',
                toolId,
                toolName,
                status,
                result,
                sequence: index || processLog.length + 1, // 使用后端发送的 index 作为 sequence
                timestamp,
              };

              if (existingIndex !== -1) {
                // 更新现有步骤
                const updatedLog = [...processLog];
                updatedLog[existingIndex] = toolResultStep;
                return updatedLog;
              } else {
                // 添加新步骤
                return [...processLog, toolResultStep];
              }
            })
          );
          break;
        }

        case 'tool_error': {
          const data = eventData as ToolErrorEvent & { index?: number };

          // 无状态处理：直接使用事件中的数据，保持后端发送的原始字段名
          const { toolId, toolName, status, result, timestamp, messageId, conversationId, index } = data;

          set(messagesAtom, (prev) =>
            messageUtils.updateProcessLog(prev, conversationId, messageId, (processLog) => {
              // 检查是否已经存在相同 toolId 的 tool_error 步骤
              const existingIndex = processLog.findIndex(
                step => step.type === 'tool_error' && step.toolId === toolId
              );

              const toolErrorStep: ProcessStep = {
                type: 'tool_error',
                toolId,
                toolName,
                status,
                result,
                sequence: index || processLog.length + 1, // 使用后端发送的 index 作为 sequence
                timestamp,
              };

              if (existingIndex !== -1) {
                // 更新现有步骤
                const updatedLog = [...processLog];
                updatedLog[existingIndex] = toolErrorStep;
                return updatedLog;
              } else {
                // 添加新步骤
                return [...processLog, toolErrorStep];
              }
            })
          );
          break;
        }

        case 'intent_processing_complete': {
          const data = eventData as any; // IntentProcessingCompleteEvent
          const { messageId, conversationId, timestamp } = data;

          set(messagesAtom, (prev) =>
            messageUtils.updateMessage(prev, conversationId, messageId, (message) => {
              const processLog = message.processStep || [];

              // 找到最后一个 text 类型的 processLog 条目
              const lastTextIndex = processLog.map((step, index) => ({ step, index }))
                .filter(({ step }) => step.type === 'text')
                .pop()?.index;

              if (lastTextIndex !== undefined) {
                const lastTextStep = processLog[lastTextIndex] as ProcessStep & { type: 'text' };

                // 将最后一个 text 的内容移动到 message.content
                const updatedContent = [{ type: 'text' as const, text: lastTextStep.text }];

                // 删除最后一个 text 类型的 processLog 条目
                const updatedProcessLog = processLog.filter((_, index) => index !== lastTextIndex);

                const res: Message = {
                  ...message,
                  content: updatedContent,
                  processStep: updatedProcessLog,
                  status: 'completed',
                  timestamp: timestamp || new Date().toISOString(),
                  contentUpdateTime: new Date().toISOString(),
                };

                console.log('handleStreamingEventV2: intent_processing_complete', res);
                return res
              }

              // 如果没有找到 text 步骤，只更新状态
              return {
                ...message,
                status: 'completed',
                timestamp: timestamp || new Date().toISOString(),
                contentUpdateTime: new Date().toISOString(),
              };
            })
          );

          // 移除对流式状态的重置
          break;
        }

        case 'intent_processing_error': {
          const data = eventData as any; // IntentProcessingErrorEvent
          const { messageId, conversationId, timestamp } = data;

          set(messagesAtom, (prev) =>
            messageUtils.updateMessage(prev, conversationId, messageId, (message) => ({
              ...message,
              content: [],
              status: 'error',
              timestamp: timestamp || new Date().toISOString(),
              contentUpdateTime: new Date().toISOString(),
            }))
          );

          // 移除对流式状态的重置
          break;
        }

        case 'conversation_title_generated': {
          const data = eventData as ConversationTitleGeneratedEvent;
          const { title, conversationId, timestamp } = data;

          console.log('conversation_title_generated 事件已触发:', data);

          // 更新对话标题
          set(updateConversationAtom, {
            conversationId,
            updates: {
              title,
              updatedAt: timestamp || new Date().toISOString(),
            }
          });
          break;
        }

        case 'message_stop': {
          const data = eventData as MessageStopEvent;

          console.log('message_stop 事件已触发:', data);

          // 标记消息流结束，主要用于日志记录
          // 实际的消息完成由 intent_processing_complete 处理
          // 这里不需要特殊处理，只记录日志即可
          break;
        }

        default:
          console.debug('未处理的V3事件类型:', event.type, eventData);
      }
    } catch (error) {
      console.error('处理V3流式事件出错:', error);
      // 移除对流式状态的重置
    }
  }
);
